/* ════════════════════════════════════════════════════
 *  <kik-swiper> — reusable Swiper slider web component.
 *
 *  Wraps the global Swiper build (assets/kik-swiper.js → window.Swiper).
 *  Loaded per-section via component-kik-swiper.js.
 *
 *  Configuration is a child <script type="application/json"> — chosen over
 *  data-* attributes because Swiper options are deeply nested (navigation,
 *  pagination, breakpoints, autoplay) and flat attributes can't express
 *  that. The JSON maps 1:1 to Swiper's options object, so any Swiper option
 *  works with no change to this component.
 *
 *  Usage:
 *    <kik-swiper>
 *      <script type="application/json">
 *        {
 *          "slidesPerView": 1,
 *          "spaceBetween": 16,
 *          "loop": true,
 *          "pagination": { "el": ".swiper-pagination", "clickable": true },
 *          "navigation": { "nextEl": ".swiper-button-next", "prevEl": ".swiper-button-prev" },
 *          "breakpoints": { "750": { "slidesPerView": 2 }, "990": { "slidesPerView": 3 } }
 *        }
 *      </script>
 *      <div class="swiper">
 *        <div class="swiper-wrapper">
 *          <div class="swiper-slide">…</div>
 *        </div>
 *        <div class="swiper-pagination"></div>
 *        <button class="swiper-button-prev" type="button"></button>
 *        <button class="swiper-button-next" type="button"></button>
 *      </div>
 *    </kik-swiper>
 *
 *  All bundled modules (Navigation, Pagination, Autoplay) are passed every
 *  time — a module stays inert unless its config key is present. To use a
 *  module not in the build, add it to src/js/kik-swiper.js and rebuild.
 *
 *  The live Swiper instance is exposed as `element.swiper`.
 * ════════════════════════════════════════════════════ */

if (!customElements.get('kik-swiper')) {
  class KikSwiper extends HTMLElement {
    connectedCallback() {
      if (this.swiper) return; // guard a repeat connect on the same instance

      const container = this.querySelector('.swiper');
      if (!container) return;

      if (!window.Swiper) {
        console.warn('[kik-swiper] window.Swiper missing — is kik-swiper.js loaded?');
        return;
      }

      this.swiper = new window.Swiper(container, {
        modules: Object.values(window.SwiperModules || {}),
        ...this.#readConfig(),
      });
    }

    disconnectedCallback() {
      this.swiper?.destroy(true, true);
      this.swiper = null;
    }

    /* Parse the child <script type="application/json"> options block.
       Missing or malformed → {} (Swiper runs with its own defaults). */
    #readConfig() {
      const script = this.querySelector('script[type="application/json"]');
      if (!script) return {};

      try {
        return JSON.parse(script.textContent);
      } catch (error) {
        console.warn('[kik-swiper] invalid JSON config — using defaults.', error);
        return {};
      }
    }
  }

  customElements.define('kik-swiper', KikSwiper);
}
